import { Download, Printer, QrCode as QrCodeIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLabelFormats } from '@/features/scan/queries';
import { scanApi } from '@/features/scan/service';
import {
  LABEL_FORMATS,
  type LabelFormat,
  type ScannableEntityType,
} from '@/features/scan/types';

interface QrCodeDisplayProps {
  entityType: ScannableEntityType;
  entityId: string;
  /** Render size of the SVG viewport in pixels. Defaults to 192. */
  size?: number;
  /** Hides the section card chrome and renders just the inner cluster
   *  — useful when the parent already provides a section. */
  bare?: boolean;
}

/**
 * QrCodeDisplay — visual QR + thermal label print/download cluster.
 *
 * Backed by Phase 5c endpoints:
 *   GET /qr/:type/:id?format=svg|png    — image bytes
 *   GET /labels/:type/:id?format=...    — printable PDF
 *
 * The SVG is loaded directly via `<img src=URL>`: the browser handles
 * caching and the operator can right-click → save. The "Print label"
 * button opens the PDF in a new tab so the OS print dialog handles
 * the rest — no custom print pipeline.
 *
 * The label format selector pulls its options from the backend via
 * useLabelFormats, with a static fallback to LABEL_FORMATS so the
 * UI is usable even before the metadata query resolves.
 */
export function QrCodeDisplay({
  entityType,
  entityId,
  size = 192,
  bare = false,
}: QrCodeDisplayProps) {
  const { t } = useTranslation();
  const { data: formatDescriptors } = useLabelFormats();
  const [format, setFormat] = useState<LabelFormat>('dymo32x57');

  const formatOptions = formatDescriptors?.length
    ? formatDescriptors.map((d) => d.format)
    : ([...LABEL_FORMATS] as LabelFormat[]);

  const svgSrc = scanApi.qrSvgUrl(entityType, entityId, size * 2);
  const pngHref = scanApi.qrPngUrl(entityType, entityId, 600);
  const labelHref = scanApi.labelPdfUrl(entityType, entityId, format);

  const inner = (
    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
      <img
        src={svgSrc}
        alt={t('qrCode.alt', { entityType })}
        width={size}
        height={size}
        className="rounded-md border bg-white p-2"
        style={{ width: size, height: size }}
      />
      <div className="flex flex-1 flex-col gap-2">
        <div className="space-y-1">
          <label
            htmlFor={`qr-format-${entityId}`}
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            {t('qrCode.formatLabel')}
          </label>
          <select
            id={`qr-format-${entityId}`}
            value={format}
            onChange={(e) => setFormat(e.target.value as LabelFormat)}
            className="flex h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {formatOptions.map((f) => (
              <option key={f} value={f}>
                {t(`qrCode.format.${f}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <a
              href={labelHref}
              target="_blank"
              rel="noreferrer"
            >
              <Printer aria-hidden />
              {t('qrCode.printLabel')}
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href={pngHref} download={`${entityType}_${entityId}.png`}>
              <Download aria-hidden />
              {t('qrCode.downloadPng')}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );

  if (bare) return inner;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <QrCodeIcon className="h-4 w-4" aria-hidden />
          {t('qrCode.sectionTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
